import React, { FC, ReactNode } from "react"
import Image from "next/image"
import styled from "styled-components"
import { rgba, rem } from "polished"
import { BrandColor, theme } from "../../util/styles/theme"
import {
  SectionCut,
  sectionCutHeightPercentageOfWidth,
  sectionCutHeightRemIe,
} from "../section-cut/SectionCut"
import { ButtonGroup } from "../button-group/ButtonGroup"
import { SingleColLayout } from "../single-col-layout/SingleColLayout"

const Container = styled.div`
  position: relative;
`

const Layers = styled.div`
  display: flex;

  > :not(:first-child) {
    margin-left: -100%;
  }
  overflow: hidden;
`

interface ImageContainerProps {
  brightness?: string
}

const ImageContainer = styled.div<ImageContainerProps>`
  filter: ${({ brightness }) => brightness && `brightness(${brightness})`};
  width: 100%;
  height: ${rem(496)};

  ${theme.breakpoints.md} {
    height: ${rem(635)};
  }

  // Hide the image on IE10+, because it would be stretched
  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    & img {
      display: none !important;
    }

    filter: none;
    background: ${theme.colors.roseGold};
  }
`

const Content = styled.div`
  align-items: center;
  color: ${theme.colors.white};
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  width: 100%;
  z-index: 1;

  padding-bottom: ${sectionCutHeightPercentageOfWidth};
  // Target IE10+
  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    // The section cut has a constant height on IE
    margin-top: -${sectionCutHeightRemIe};
    padding-bottom: 0;
  }
`

const Title = styled.h1`
  ${theme.textStyle("heading", "xlarge", "extrabold")}

  ${theme.breakpoints.md} {
    ${theme.textStyle("heading", "xxlarge", "extrabold")}
  }

  ${theme.textReadabilityOutline(
    theme.colors.black,
    rgba(theme.colors.greyDark, 0.45),
  )}
`

const Description = styled.div`
  ${theme.textStyle("paragraph", "medium", "medium")}

  ${theme.breakpoints.sm} {
    ${theme.textStyle("paragraph", "xlarge", "medium")}
  }

  ${theme.textReadabilityOutline(
    theme.colors.black,
    rgba(theme.colors.greyDark, 0.45),
  )}

  strong {
    font-weight: ${theme.typography.paragraph.weights.bold};
  }
`

const StyledButtonGroup = styled(ButtonGroup)`
  margin-top: ${rem(46)};
  justify-content: center;
`

export interface ImageSpec {
  brightness?: string
  originalHeight: number
  originalWidth: number
  src: string
}

export interface HeroImageProps {
  buttons?: ReactNode
  description?: ReactNode
  image: ImageSpec
  sectionCutColor?: BrandColor
  title: ReactNode
}

export const HeroImage: FC<HeroImageProps> = ({
  buttons,
  description,
  image,
  title,
  sectionCutColor = "white",
}) => (
  <Container>
    <Layers>
      <ImageContainer brightness={image.brightness}>
        <Image
          src={image.src}
          width={image.originalWidth}
          height={image.originalHeight}
          alt=""
          layout="fill"
          objectFit="cover"
          priority
        />
      </ImageContainer>
      <Content>
        <SingleColLayout>
          {title && <Title>{title}</Title>}
          {description && <Description>{description}</Description>}
          {buttons && (
            <StyledButtonGroup xs="vertical" sm="horizontal">
              {buttons}
            </StyledButtonGroup>
          )}
        </SingleColLayout>
      </Content>
    </Layers>
    <SectionCut color={sectionCutColor} cutAngleLeftToRight="uphill" />
  </Container>
)
